import { registerSW } from 'virtual:pwa-register';
import { scheduleIdle } from '@/lib/idle';

/**
 * Service worker registration, and the schedule on which the app notices that a
 * new version has been deployed.
 *
 * The worker itself is generated by vite-plugin-pwa; `vite.config.ts` explains
 * why it is `autoUpdate` and what was broken before. This module is the other
 * half: *when* the app looks for a new version, and where it deliberately does
 * not run at all.
 *
 * Registration is what makes the app work offline, and it is also the thing most
 * capable of pinning users to a build from last week. Both properties come from
 * the same cache, so the update schedule below is not a nicety — it is the only
 * thing keeping the offline cache from becoming a trap.
 */

/**
 * An hour. The interval is the fallback, not the main mechanism: a backgrounded
 * page is frozen by mobile browsers and its timers stop, so on a phone this
 * fires only while Dove is actually on screen. The visibility check below is
 * what catches the case that matters.
 */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Inside the iOS and Android shells the bundle is not fetched over HTTP at all —
 * Capacitor serves `dist/` from the app container, and it is replaced wholesale
 * by a store update or an OTA bundle swap (docs/3 § OTA live updates). A service
 * worker there would be a second cache layer competing with the one Capacitor
 * already owns, with nothing to gain and a stale bundle to lose.
 *
 * Detected from the global the native shell injects rather than by importing
 * `@capacitor/core`, which is a devDependency here and should not be pulled into
 * the web bundle for one boolean.
 */
function isCapacitorNative(): boolean {
  const global = window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  return global.Capacitor?.isNativePlatform?.() === true;
}

/**
 * Asks the browser to re-check the worker script.
 *
 * The fetch before `update()` is not redundant. `update()` against a server that
 * answers with a captive-portal page, a 404 or an error can leave the
 * registration in a state the browser resolves by discarding it, so we confirm
 * the worker URL really is being served first and only then update. This is the
 * recipe vite-plugin-pwa documents for periodic updates.
 */
async function checkForUpdate(
  swUrl: string,
  registration: ServiceWorkerRegistration,
): Promise<void> {
  // A worker mid-install is already doing this; asking again achieves nothing.
  if (registration.installing) return;
  if (!navigator.onLine) return;

  try {
    const response = await fetch(swUrl, {
      cache: 'no-store',
      headers: { 'cache-control': 'no-cache' },
    });
    if (response.status !== 200) return;
    await registration.update();
  } catch {
    /*
     * Offline, or a network that lied about being online. Nothing to handle:
     * the next visibility change or interval tick tries again, and until then
     * the cached app keeps working, which is the whole point of having it.
     */
  }
}

/**
 * Registers the worker and starts the update schedule. Safe to call once, from
 * the app entry.
 *
 * Under `autoUpdate` the page reloads itself when a *newly installed* worker
 * takes over — never on a first install, only on a genuine update. The reload is
 * how the running page picks up the new HTML and bundle it is now being served;
 * without it the app would sit on the old markup until the next cold start.
 *
 * The honest cost: if a new build lands while someone is mid-sentence in the
 * composer, the reload will take the draft with it, because drafts are not
 * persisted yet. That is a narrow window — the checks fire after load settles
 * and on returning to the app, not continuously — and it is the right trade against
 * users silently stranded on an old build, which is what was actually happening.
 * When drafts are persisted this becomes a non-issue.
 */
export function registerServiceWorker(): void {
  if (isCapacitorNative()) return;

  const register = () =>
    registerSW({
    /*
     * This call itself happens after `load` and idle below. `immediate` therefore
     * needs no override: the plugin sees a completed document and registers now.
     */
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;

      const check = () => {
        void checkForUpdate(swUrl, registration);
      };

      setInterval(check, UPDATE_CHECK_INTERVAL_MS);

      /*
       * THE ONE THAT MATTERS on a phone. An installed PWA is almost never cold
       * started — it is resumed from the home screen, days later, with the same
       * frozen page and the same worker. Without this, that session could run
       * indefinitely without ever asking whether a new build exists.
       */
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });

      // Coming back from a tunnel or a dead hotel network is a free chance to look.
      window.addEventListener('online', check);
    },
    onRegisterError(error) {
      /*
       * Registration failing is not fatal — the app runs from the network like
       * any other site — but it is silent, and a silent failure here looks
       * exactly like the caching bug this file exists to prevent.
       */
      console.error('[dove] service worker registration failed', error);
    },
    });

  /*
   * INSTALLING THE OFFLINE CACHE IS IMPORTANT AND NOT CRITICAL-PATH WORK.
   *
   * The generated precache is roughly three MiB before source maps. Registering
   * at `load` can begin fetching it while the session and first feed page are
   * still resolving, because API fetches do not hold the load event open. Wait
   * for load, then for idle, with a five-second ceiling so a busy app still gets
   * its worker. Existing workers continue serving throughout this delay.
   */
  const afterLoad = () => {
    scheduleIdle(() => void register(), { timeout: 5_000, fallbackDelay: 2_000 });
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
}
